import { type FC, type FormEvent } from 'react';

import {
  IconLoader2,
  IconSettingsPlus,
} from '@tabler/icons-react';
import { ButtonField } from '@wk/components-react16';

import type { Application } from '@/domains/application/application.types';
import type { ApplicationConfiguration } from '@/domains/application/applicationConfiguration.types';
import ComboBox from '@/shared/ui/Combobox';

interface OnboardApplicationConfigureFormProps {
  applications: Application[];
  isCreatingConfiguration: boolean;
  isFetchingApplicationConfigurations: boolean;
  isSavingConfiguration: boolean;
  latestConfiguration: ApplicationConfiguration | null;
  selectedApplication: string;
  onSelectedApplicationChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const OnboardApplicationConfigureForm: FC<OnboardApplicationConfigureFormProps> = ({
  applications,
  isCreatingConfiguration,
  isFetchingApplicationConfigurations,
  isSavingConfiguration,
  latestConfiguration,
  selectedApplication,
  onSelectedApplicationChange,
  onSubmit,
}) => {
  const comboBoxOptions = applications.map((application) => ({
    label: (
      <span className="flex flex-col gap-1">
        <span className="text-sm font-medium">{application.name}</span>
        <span className="text-xs text-muted-foreground">
          {application.bitId}
        </span>
      </span>
    ),
    value: application.bitId,
    valueLabel: application.name,
  }));

  const isDraftConfiguration = latestConfiguration?.isDraft ?? false;
  const isMutatingConfiguration =
    isCreatingConfiguration || isSavingConfiguration;

  return (
    <form
      className="onboard-form flex items-center gap-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-3"
      onSubmit={onSubmit}
    >
      <div className="flex flex-col max-sm:w-full">
        <ComboBox
          label=""
          labelFor="applications"
          options={comboBoxOptions}
          disabled={isFetchingApplicationConfigurations}
          value={selectedApplication}
          onChange={onSelectedApplicationChange}
          filter={(value, search) => {
            const option = applications.find(
              (application) => application.bitId === value,
            );

            if (!option) return false;

            const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const searchRegex = new RegExp(escapedSearch, 'i');

            return (
              searchRegex.test(option.bitId) ||
              searchRegex.test(option.name)
            );
          }}
          placeholder="Select by Bit ID or Name"
          hasLoadingIcon={isFetchingApplicationConfigurations}
        />
      </div>
      {!isDraftConfiguration ? (
        <div className="subject-onboarding-form-actions">
          <ButtonField size="large">
            <button
              disabled={
                !selectedApplication ||
                isFetchingApplicationConfigurations ||
                isMutatingConfiguration
              }
            >
              <IconSettingsPlus size={20} />
              <span className="flex items-center gap-2">
                <span>
                  {latestConfiguration && !latestConfiguration.isDraft
                    ? 'Reconfigure'
                    : 'Configure'}
                </span>
                {isMutatingConfiguration ? (
                  <IconLoader2 size={16} className="animate-spin" />
                ) : null}
              </span>
            </button>
          </ButtonField>
        </div>
      ) : null}
    </form>
  );
};

export default OnboardApplicationConfigureForm;
